import {
  useAccountBalances,
  useInitiateTopupMutation,
  useTopupConfig,
} from '@/src/api/payg';
import {
  ErrorSection,
  LoadingSpinner,
  PageWithNavigation,
} from '@/src/components';
import {
  RemoteAlerts,
  useSupportBanner,
} from '@/src/components/payg/RemoteAlerts';
import { PAYG_EVENTS } from '@/src/constants/analytics';
import {
  PAYG_ACCOUNT_TOPUP_PAGE_ERROR,
  PAYMENT_ERROR,
} from '@/src/constants/error-section-ids';
import { ROUTE_HOME } from '@/src/constants/routes';
import AnalyticsService from '@/src/services/analyticsService';
import { reportError } from '@/src/services/datadog';
import { getErrorDetails } from '@/src/utils/errorUtils';
import { Margin } from '@ovotech/element-v5';
import React, { useEffect, useMemo, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom-v5';
import { PAYGTopupPageContent } from './PAYGTopupPageContent';
import { HeadingCard, TopupHeading } from './styles';

export const PAYGTopupPage = () => {
  const { msn } = useParams<{ msn: string }>();
  const { banner, disableTopups } = useSupportBanner();
  const history = useHistory();
  const [isMutationError, setIsMutationError] = useState(false);

  const config = useTopupConfig();
  const balances = useAccountBalances();
  const { mutateAsync } = useInitiateTopupMutation();

  const meterBalance = useMemo(
    () => balances.data?.balances.find(b => b.msn === msn),
    [balances.data, msn],
  );

  useEffect(() => {
    AnalyticsService.dispatch(
      ...PAYG_EVENTS.topupInputViewed({ fuel: meterBalance?.fuel }),
    );
  }, [meterBalance?.fuel]);

  useEffect(() => {
    if (config.error) {
      const { code, message } = getErrorDetails(config.error);
      AnalyticsService.dispatch(
        ...PAYG_EVENTS.topupInputError({ code, message, source: 'config' }),
      );
    }
    if (balances.error) {
      const { code, message } = getErrorDetails(balances.error);
      AnalyticsService.dispatch(
        ...PAYG_EVENTS.topupInputError({ code, message, source: 'balances' }),
      );
    }
  }, [balances.error, config.error]);

  if (disableTopups) {
    return <Redirect to={ROUTE_HOME} />;
  }

  if (config.isLoading || balances.isLoading) {
    return (
      <PageWithNavigation title="PAYG Topup">
        <Margin top={8} />
        {banner && <RemoteAlerts {...banner} />}
        <Margin top={8} />
        <LoadingSpinner />
      </PageWithNavigation>
    );
  }

  if (config.isError || balances.isError || !meterBalance || !config.data) {
    return (
      <PageWithNavigation title="PAYG Topup">
        <Margin top={8} />
        {banner && <RemoteAlerts {...banner} />}
        <Margin top={8} />
        <ErrorSection id={PAYG_ACCOUNT_TOPUP_PAGE_ERROR} alertLevel="error" />
      </PageWithNavigation>
    );
  }

  const heading = `Top up your ${meterBalance.fuel.toLowerCase()}`;

  return (
    <PageWithNavigation title={heading}>
      <Margin top={8} />
      {banner && <RemoteAlerts {...banner} />}
      <Margin top={8} bottom={4}>
        <HeadingCard>
          <TopupHeading>{heading}</TopupHeading>
        </HeadingCard>
      </Margin>

      {isMutationError && (
        <Margin top={6}>
          <ErrorSection id={PAYMENT_ERROR} alertLevel="error" />
        </Margin>
      )}

      <PAYGTopupPageContent
        {...config.data}
        currentBalanceInPence={Math.round(meterBalance.balance * 100)}
        onBack={history.goBack}
        onContinue={async amountInPence => {
          setIsMutationError(false);
          AnalyticsService.dispatch(
            ...PAYG_EVENTS.topupInputPressedSubmit({
              amountInPence,
              fuel: meterBalance.fuel,
            }),
          );
          try {
            const response = await mutateAsync({
              msn: meterBalance.msn,
              mpxn: meterBalance.mpxn,
              fuel: meterBalance.fuel,
              topUpPoundsInt: Math.round(amountInPence / 100),
              channel: 'ovo_account_web',
            });

            if (response.paymentSessionUrl) {
              AnalyticsService.dispatch(
                ...PAYG_EVENTS.topupInputNavigatedToPayment({
                  amountInPence,
                  fuel: meterBalance.fuel,
                  topupSessionId: response.topupSessionId,
                }),
              );
              window.location.assign(response.paymentSessionUrl);
            }
          } catch (error) {
            // TODO: Check with Josh if we have UI for this
            reportError(error);
            const { code, message } = getErrorDetails(error);
            AnalyticsService.dispatch(
              ...PAYG_EVENTS.topupInputInitiateFailed({
                amountInPence,
                fuel: meterBalance.fuel,
                code,
                message,
              }),
            );
            setIsMutationError(true);
          }
        }}
      />
    </PageWithNavigation>
  );
};
